import { Sparkles, Play } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { RichUiGuideContent } from "@/app/api/types/chat-sessions";
import { getGuideWalkthrough } from "@/lib/guide/walkthroughs";
import { startGuideFromPayload } from "@/lib/guide/fromPayload";

/**
 * Inline chat card for a ui_guide payload. The guide does NOT auto-run — this
 * card is the opt-in: it names the scenario ("Guide pour …") and the user taps
 * Start to launch it (which also closes the chat). Persistent in the
 * conversation, so it doubles as the replay affordance.
 */
export function GuideChip({
  content,
}: Readonly<{ content: RichUiGuideContent }>) {
  const { t } = useTranslation();
  const walkthrough = content.walkthrough_id
    ? getGuideWalkthrough(content.walkthrough_id)
    : undefined;
  // Scenario name (i18n'd, lowercase) rendered as "Guide pour <name>".
  // Use the hook-bound `t` so the title re-renders when the language changes.
  const name =
    content.title ?? (walkthrough ? t(walkthrough.titleKey) : undefined);
  const title = name ? t("guide.chipLabel", { name }) : t("guide.defaultTitle");

  return (
    <div
      data-testid="guide-chip"
      className="inline-flex items-center gap-2.5 rounded-lg border border-guide/30 bg-guide/10 px-3 py-2"
    >
      <Sparkles className="h-4 w-4 shrink-0 text-guide" />
      <span className="truncate max-w-[220px] text-xs font-medium text-guide">
        {title}
      </span>
      <button
        type="button"
        data-testid="guide-chip-start"
        onClick={() => startGuideFromPayload(content, { replay: true })}
        className="inline-flex shrink-0 items-center gap-1 rounded-md bg-guide px-2.5 py-1 text-[11px] font-semibold text-guide-foreground hover:bg-guide/90"
      >
        <Play className="h-3 w-3" />
        {t("guide.start")}
      </button>
    </div>
  );
}
